import React, {
  useState, useCallback, useEffect, useMemo,
} from 'react';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import * as HTML5BackendModule from 'react-dnd-html5-backend';

const ItemType = 'IMAGE_ITEM';

function DraggableImage({
  item, from, index, badgeNumber, compact = false, fillContainer = false,
}) {
  let imageWidth = 114;
  let imageHeight = 114;
  if (compact) {
    imageWidth = 114;
    imageHeight = 114;
  }
  if (fillContainer) {
    imageWidth = '100%';
    imageHeight = '100%';
  }

  const [{ isDragging }, drag] = useDrag({
    type: ItemType,
    item: { id: item.id, from, index },
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  });

  return (
    <div
      ref={drag}
      style={{
        opacity: isDragging ? 0.5 : 1,
        display: fillContainer ? 'block' : 'inline-block',
        margin: fillContainer ? 0 : 6,
        width: fillContainer ? '100%' : 'auto',
        height: fillContainer ? '100%' : 'auto',
        textAlign: 'center',
        cursor: 'grab',
        position: 'relative',
      }}
    >
      <div style={{
        position: 'relative',
        display: fillContainer ? 'block' : 'inline-block',
        width: fillContainer ? '100%' : 'auto',
        height: fillContainer ? '100%' : 'auto',
      }}>
        {/* numbered badge for thumbnails (use stable badgeNumber when available) */}
        {(badgeNumber != null) && (
          <div style={{
            position: 'absolute',
            top: -8,
            left: -8,
            width: compact ? 18 : 22,
            height: compact ? 18 : 22,
            borderRadius: (compact ? 18 : 22) / 2,
            background: 'rgba(0,0,0,0.55)',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: compact ? 11 : 12,
            lineHeight: 1,
            boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
          }}>
            {badgeNumber}
          </div>
        )}

        <img src={item.src} alt={item.label || item.id} style={{
          width: imageWidth,
          height: imageHeight,
          objectFit: 'cover',
          display: 'block',
          borderRadius: 4,
          border: '1px solid #ddd',
        }} />
      </div>
      {/* {item.label && <div style={{ fontSize: 12 }}>{item.label}</div>} */}
    </div>
  );
}

function TargetSlot({
  targetId,
  label,
  placedItem,
  expectedSrc,
  slotLetter = '?',
  badgeNumber = null,
  onDropItem,
  items,
  showExpected,
}) {
  // neutral border styling (do not reveal correctness)
  const boxBorder = placedItem ? '2px solid #0000001F' : '2px dashed #0000001F';
  const [{ isOver }, drop] = useDrop({
    accept: ItemType,
    collect: (monitor) => ({ isOver: monitor.isOver() }),
    drop: (dragged) => onDropItem(dragged, targetId),
  });

  const renderPlaced = () => {
    if (!placedItem) return null;
    const resolved = typeof placedItem === 'string' ? items.find((it) => it.id === placedItem) || { id: placedItem, src: '', label: placedItem } : placedItem;
    return (
      <DraggableImage
        key={resolved.id}
        item={resolved}
        from={targetId}
        compact
        fillContainer
        badgeNumber={badgeNumber}
      />
    );
  };

  return (
    <div ref={drop} style={{
      position: 'relative', width: '100%', maxWidth: 332, height: 138, padding: 12, borderRadius: 6, background: '#fff', border: '1px solid #e6f0f6', boxSizing: 'border-box',
    }}>

      {isOver && <div style={{
        position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.02)', borderRadius: 6,
      }} />}
      <div style={{
        display: 'flex', gap: 12, alignItems: 'center', height: '100%',
      }}>
        <div style={{
          width: 108, height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{
            width: 114, height: 114, border: boxBorder, borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F9F9FD', boxSizing: 'border-box',
          }}>
            {placedItem ? (
              renderPlaced()
            ) : (
              showExpected && expectedSrc ? (
                <img src={expectedSrc} alt={`${label}-expected`} style={{
                  width: 114, height: 114, objectFit: 'cover', borderRadius: 4,
                }} />
              ) : (
                <img src={'assets/img/insertImg.svg'} alt="insert" style={{ width: 40, height: 30, objectFit: 'contain' }} />
              )
            )}
          </div>
        </div>

        <div style={{
          flex: 1, display: 'flex', alignItems: 'center', minWidth: 0,
        }}>
          <div style={{
            fontSize: 15, fontWeight: 500, display: 'flex', alignItems: 'center',
          }}>
            <span style={{ marginRight: 8, fontWeight: 700 }}>{slotLetter}.</span>
            <span>{label}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function SourceDropArea({ children, onReturn }) {
  const [{ isOver }, drop] = useDrop({
    accept: ItemType,
    collect: (monitor) => ({ isOver: monitor.isOver() }),
    drop: (dragged) => onReturn(dragged),
  });

  return (
    <div ref={drop} style={{ position: 'relative' }}>
      {isOver && <div style={{
        position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.02)', borderRadius: 6,
      }} />}
      {children}
    </div>
  );
}

export default function ImageDragDrop({
  id, items = [], targets = [], answers = [], initialPlacement = {}, value = null, onChange = () => {}, restrictEdit = false, showAns = false, showExpected = false, resetSignal, showReset = true,
}) {
  const activeTargets = useMemo(() => {
    if (!Array.isArray(targets)) return [];
    if (!Array.isArray(answers) || answers.length === 0) return targets;

    const filtered = targets.filter((target, idx) => {
      const expected = answers[idx];
      return expected !== null && typeof expected !== 'undefined' && expected !== '';
    });

    return filtered.length > 0 ? filtered : targets;
  }, [targets, answers]);

  // determine desktop vs mobile for grid layout (2 columns on desktop)
  const [isDesktop, setIsDesktop] = useState(typeof window !== 'undefined' ? window.innerWidth >= 768 : true);
  useEffect(() => {
    function onResize() {
      setIsDesktop(window.innerWidth >= 768);
    }
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // badgeMapRef stores the original ordering of `items` so badge numbers
  // remain stable even when source ordering changes as items are placed.
  const badgeMapRef = React.useRef({});
  useEffect(() => {
    // Ensure every item has a stable badge number. Preserve existing
    // assignments and assign new sequential numbers to previously-unseen items.
    const map = badgeMapRef.current || {};
    const existingNums = Object.keys(map).filter((k) => k !== '_initialized').map((k) => map[k]).filter((v) => typeof v === 'number');
    let next = existingNums.length ? Math.max(...existingNums) + 1 : 1;
    items.forEach((it, i) => {
      if (map[it.id] == null) {
        map[it.id] = next;
        next += 1;
      }
    });
    map._initialized = true;
    badgeMapRef.current = map;
  }, [items]);
  // helper to resolve placement entries (id or object) to full item objects
  const resolveItem = (itVal, itemList) => {
    if (!itVal) return null;
    if (typeof itVal === 'string') return itemList.find((it) => it.id === itVal) || null;
    if (typeof itVal === 'object') {
      const found = itemList.find((it) => it.id === itVal.id);
      return found || itVal;
    }
    return null;
  };

  // compute initial placement/source synchronously so first render matches provided props
  const computeInitial = useCallback(() => {
    const placedIds = new Set();
    const place = {};
    const sourcePlacement = value || initialPlacement;
    activeTargets.forEach((t) => {
      const pid = sourcePlacement && Object.prototype.hasOwnProperty.call(sourcePlacement, t.id) ? sourcePlacement[t.id] : null;
      const resolved = resolveItem(pid, items);
      place[t.id] = resolved;
      if (resolved && resolved.id) placedIds.add(resolved.id);
    });
    const src = items.filter((it) => !placedIds.has(it.id));
    return { place, src };
  }, [items, activeTargets, initialPlacement, value]);

  const initial = useMemo(() => computeInitial(), [computeInitial]);
  const [sourceItems, setSourceItems] = useState(initial.src);
  const [placement, setPlacement] = useState(initial.place);
  const [version, setVersion] = useState(0);

  useEffect(() => {
    // If parent controls placement via `value`, update internal state accordingly.
    // If `value` is null (uncontrolled), do not overwrite internal state on re-renders.
    if (value === null) return;
    const { place, src } = computeInitial();
    try {
      const placeJson = JSON.stringify(Object.keys(place).sort().reduce((acc, k) => { acc[k] = place[k] ? place[k].id : null; return acc; }, {}));
      const srcJson = JSON.stringify(src.map((s) => s.id));
      const curPlaceJson = JSON.stringify(Object.keys(placement).sort().reduce((acc, k) => { acc[k] = placement[k] ? placement[k].id : null; return acc; }, {}));
      const curSrcJson = JSON.stringify(sourceItems.map((s) => s.id));
      if (placeJson !== curPlaceJson) setPlacement(place);
      if (srcJson !== curSrcJson) setSourceItems(src);
    } catch (e) {
      setPlacement(place);
      setSourceItems(src);
    }
  }, [items, activeTargets, initialPlacement, value]);

  const commitChange = useCallback((newPlacement, newSource) => {
    setPlacement(newPlacement);
    setSourceItems(newSource);
    setVersion((v) => v + 1);
    // report placement as mapping targetId -> itemId | null, plus remaining source ids
    const report = { placement: {}, source: newSource.map((s) => s.id) };
    const slotAnswers = [];
    const targetOrder = Array.isArray(activeTargets) && activeTargets.length > 0
      ? activeTargets.map((t) => t.id)
      : Object.keys(newPlacement);

    targetOrder.forEach((k, idx) => {
      report.placement[k] = newPlacement[k] ? newPlacement[k].id : null;
      const letter = String.fromCharCode(65 + idx);
      const placed = newPlacement[k];
      if (placed) {
        const placedId = typeof placed === 'string' ? placed : placed.id;
        const badge = badgeMapRef.current[placedId] || null;
        slotAnswers.push(`${letter}(${badge != null ? badge : ''})`);
      } else {
        slotAnswers.push(`${letter}()`);
      }
    });
    report.slotAnswers = slotAnswers;
    // defer onChange to avoid triggering parent re-renders during DnD lifecycle
    if (onChange) setTimeout(() => onChange(report), 0);
  }, [onChange, activeTargets]);

  const handleDrop = useCallback((dragged, toTargetId) => {
    if (restrictEdit) return;
    setPlacement((prev) => {
      const newPlacement = { ...prev };
      const newSource = [...sourceItems];

      // remove dragged item from its origin
      if (dragged.from === 'source') {
        // remove from source array
        const idx = newSource.findIndex((s) => s.id === dragged.id);
        if (idx !== -1) newSource.splice(idx, 1);
      } else {
        // dragged from another target
        newPlacement[dragged.from] = null;
      }

      // if target occupied, move that item back to source
      const existing = newPlacement[toTargetId];
      if (existing) {
        newSource.unshift(existing);
      }

      // find dragged item object
      const draggedItem = items.find((it) => it.id === dragged.id) || { id: dragged.id, src: '', label: '' };
      newPlacement[toTargetId] = draggedItem;

      commitChange(newPlacement, newSource);
      return newPlacement;
    });
  }, [items, sourceItems, restrictEdit, commitChange]);

  const handleReturnToSource = useCallback((dragged) => {
    if (restrictEdit) return;
    setPlacement((prev) => {
      const newPlacement = { ...prev };
      const newSource = [...sourceItems];
      if (dragged.from !== 'source') {
        const p = newPlacement[dragged.from];
        if (p && p.id === dragged.id) {
          newSource.unshift(p);
          newPlacement[dragged.from] = null;
        }
      }
      commitChange(newPlacement, newSource);
      return newPlacement;
    });
  }, [sourceItems, restrictEdit, commitChange]);

  const handleReset = useCallback(() => {
    // reset: clear all placements and return all items to source
    const place = {};
    activeTargets.forEach((t) => { place[t.id] = null; });
    const src = [...items];
    commitChange(place, src);
  }, [items, activeTargets, commitChange]);

  // allow parent to trigger reset by changing `resetSignal`
  React.useEffect(() => {
    if (typeof resetSignal !== 'undefined') {
      handleReset();
    }
  }, [resetSignal]);

  const ResolvedBackend = HTML5BackendModule.default || HTML5BackendModule.HTML5Backend || HTML5BackendModule;
  return (
    <DndProvider backend={ResolvedBackend}>
      <div style={{
        display: 'flex', gap: 24, alignItems: 'flex-start', flexDirection: 'column',
      }}>
        <div>
          {/* make the source panel a drop target so placed images can be dragged back */}
          <SourceDropArea onReturn={handleReturnToSource}>
            <div style={{
              border: '1px solid #eee', padding: 12, minHeight: 140, background: '#fafafa',
            }}>
              {sourceItems.length === 0 && <div style={{ color: '#888' }}>No available images</div>}
              {sourceItems.map((it, idx) => {
                let bn = badgeMapRef.current[it.id];
                if (bn == null) {
                  const existingNums = Object.keys(badgeMapRef.current).filter((k) => k !== '_initialized').map((k) => badgeMapRef.current[k]).filter((v) => typeof v === 'number');
                  const next = existingNums.length ? Math.max(...existingNums) + 1 : 1;
                  badgeMapRef.current[it.id] = next;
                  bn = next;
                }
                return (
                  <DraggableImage
                    key={it.id}
                    item={it}
                    from="source"
                    index={idx}
                    badgeNumber={bn}
                  />
                );
              })}
            </div>
          </SourceDropArea>
        </div>

        <div style={{ flex: 1 }}>
          <div style={{
            display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, justifyContent: 'center',
          }}>
              {(() => {
              // If `answers` is provided, render one slot per answer (q0..qN-1).
              // Otherwise fall back to keys from `placement`.
                let targetOrder = [];
                if (Array.isArray(activeTargets) && activeTargets.length > 0) {
                  targetOrder = activeTargets.map((t) => t.id);
                } else {
                  targetOrder = Object.keys(placement || {});
                }

                return targetOrder.map((tid, index) => {
                  const tmeta = targets.find((x) => x.id === tid) || { id: tid, label: tid, img: null };
                  const raw = placement[tid];
                  const placed = raw ? (typeof raw === 'string' ? items.find((it) => it.id === raw) || null : raw) : null;
                  const badgeForTarget = placed ? badgeMapRef.current[(typeof placed === 'string' ? placed : placed.id)] : null;
                  const slotLetter = String.fromCharCode(65 + index);
                  return (
                      <div key={`${tid}-${version}`} style={{ width: '100%' }}>
                        <TargetSlot
                          targetId={tid}
                          label={tmeta.label}
                          placedItem={placed}
                          expectedSrc={tmeta.img}
                          slotLetter={slotLetter}
                          badgeNumber={badgeForTarget}
                          onDropItem={handleDrop}
                          items={items}
                          showExpected={false}
                          showAns={showAns}
                        />
                      </div>
                  );
                });
              })()}
          </div>
          {showReset !== false && (
            <div style={{ marginTop: 12 }}>
              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={handleReset}>重填</button>
            </div>
          )}
          {showAns && Object.values(placement || {}).some((p) => p != null) && (
            <div className="mt-3">
              <div className="p-2" style={{ background: '#F9D78B', border: '2px solid #F9D78B', borderRadius: 4 }}>
                <strong className="text-dark">我的答案：</strong>
                {(() => {
                  // build ordered target list same as rendering order
                  let targetOrder = [];
                  if (Array.isArray(activeTargets) && activeTargets.length > 0) {
                    targetOrder = activeTargets.map((t) => t.id);
                  } else {
                    targetOrder = Object.keys(placement || {});
                  }
                  const parts = targetOrder.map((tid, idx) => {
                    const letter = String.fromCharCode(65 + idx);
                    const placed = placement[tid];
                    if (placed) {
                      const placedId = typeof placed === 'string' ? placed : placed.id;
                      const badge = badgeMapRef.current[placedId] || '';
                      return `${letter}(${badge})`;
                    }
                    return `${letter}()`;
                  });
                  return <span className="ml-2">{parts.join(', ')}</span>;
                })()}
              </div>
            </div>
          )}
        </div>
      </div>
    </DndProvider>
  );
}
